import React, { useState, useEffect } from "react";
import TeamService from "services/TeamService";
import CustomTextArea from "components/CustomTextArea";
import AttachmentSelection from "components/AttachmentSelection/AttachmentSelection";
import * as Yup from "yup";
import Button from "components/CustomButton";
import { useFormik } from "formik";
import CustomInput from "components/CustomInput/CustomInput";
import TeamMemberSelection from "components/TeamMemberSelection";
import AttachmentPreview from "components/AttachmentPreview/AttachmentPreview";
import { Modal } from "react-responsive-modal";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import GeneralService from "services/GeneralService";
import { useTimelineContext } from "store/contexts/timelineContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useToasts } from "react-toast-notifications";
import { faPlusSquare, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import ButtonWithProgress from "components/ButtonWithProgress";
import CustomButton from "components/CustomButton";
import { useGlobalContext } from "store/contexts/globalContext";
import { useTeamActionContext } from "store/contexts/teamActionContext";

export default ({ teamId, listId }) => {
  const { microsoftTeamsVideoText } = useGlobalContext();
  const [dialogState, setDialogState] = useState(false);
  const [selectedMember, setSelectedMember] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { createTask } = useTimelineContext();
  const { addToast } = useToasts();
  const { createNewPost } = useTeamActionContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = new Date();
  const attachmentText = {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };

  useEffect(() => {
    today.setDate(today.getDate() - 1);
  }, [today]);

  const formik = useFormik({
    initialValues: {
      task: "",
      priority: "",
      date: "",
      postattachment: null,
    },
    validationSchema: Yup.object().shape(
      {
        task: Yup.string().when(["postattachment"], {
          is: (postattachment) => {
            return !postattachment;
          },
          then: Yup.string()
            .min(4, "Task name must be between 4 to 5000 characters.")
            .max(5000, "Task name should not be greater then 5000")
            .required(
              "Please enter the description OR attach media to proceed"
            ),
        }),
        postattachment: Yup.mixed().when(["task"], {
          is: (task) => {
            return !task;
          },
          then: Yup.mixed().required(
            "Please enter the description OR attach media to proceed"
          ),
        }),
        priority: Yup.string().max(
          25,
          "Maximum 25 characters are allowed for the priority field"
        ),
        date: Yup.date().min(today, "Previous date cannot selected."),
      },
      ["postattachment", "task", "priority"]
    ),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      let media = null;
      if (values.task.length < 4 || values.task.length > 5000) {
        formik.setErrors({
          task: "Task name must be between 4 to 5000 characters",
        });
        return;
      }

      if (values.postattachment) {
        const preSignedResponse = await GeneralService.getPresignedUploadURL({
          filename: values.postattachment.name,
          mime_type: values.postattachment.type,
          file_size: values.postattachment.size,
          resource_type: "team",
          resource_id: teamId,
        });

        const { key, url } = preSignedResponse.data.data;

        media = key;

        await GeneralService.uploadToS3(
          url,
          values.postattachment,
          (progress) => {
            setUploadProgress(progress);
          }
        );
      }

      setUploadProgress(100);

      try {
        const response = await TeamService.postTodoTask(teamId, listId, {
          description: values.task,
          priority: values.priority,
          due_date: values.date,
          media,
          attachment: values.postattachment,
          tag_users:
            selectedMember.length !== 0
              ? selectedMember.map((selectedMember) => selectedMember.id)
              : null,
        });

        if (values.postattachment) {
          var attachmentType = values.postattachment.type.split("/");
        }

        formik.resetForm();

        setUploadProgress(0);
        setSelectedMember([]);
        setDialogState(false);

        if (
          attachmentType &&
          attachmentType[0] === "video" &&
          attachmentType[1] !== "mp4" &&
          !microsoftTeamsVideoText
        ) {
          addToast(
            "Your task is being processed. We’ll let you know when it’s ready to view. ",
            { appearance: "info" }
          );
          return;
        }

        addToast("Your Task is created.", { appearance: "success" });

        var postData = {
          content: response.data.data.description,
          id: response.data.data.id,
          media: response.data.data.media,
          media_type_id: response.data.data.media_type_id,
          todo_list_id: JSON.parse(response.data.data.todo_list_id),
        };

        createNewPost(
          postData,
          response.data.data.team_post_id,
          response.data.data.updated_at,
          { name: "todo-list-item", id: response.data.data.post.type_id }
        );
        createTask(response.data.data);

        // window.location.reload();
        // enqueueSnackbar('Your task has been posted successfully to the list.');
      } catch (e) {
        console.log(e);
      }
    },
  });

  const closeDialog = () => {
    formik.resetForm();
    setSelectedMember([]);

    setDialogState(false);
  };

  const onAttachmentSelected = (file) => {
    formik.setFieldValue("postattachment", file);
    setShowPreview(true);
  };

  const clearAttachment = () => {
    formik.setFieldValue("postattachment", null);
  };

  return (
    <>
      <div className="d-inline-block task-left-btn">
        <Button
          onClick={() => setDialogState(true)}
          buttonClass="light-gray-btn taskCreateButton"
        >
          <FontAwesomeIcon icon={faPlusSquare} className="plus-icon" /> Create A
          Task
        </Button>
      </div>

      <Modal
        open={dialogState}
        onClose={closeDialog}
        center
        classNames={{
          modal: "createTaskModal",
        }}
        showCloseIcon={false}
      >
        <div className="modal fade popup-modal withcross create-task-popup">
          <h5 className="text-center createTaskHeading">Create a Task</h5>
          <CustomButton type="button" buttonClass="btnClose">
            <CloseIcon style={{ width: "20px" }} onClick={closeDialog} />
          </CustomButton>
          <form noValidate onSubmit={formik.handleSubmit}>
            <div style={attachmentText}>
              <CustomTextArea
                errorClass="textStyle"
                textColor={"black"}
                textAreaClass="full_w text-center mt-3 textAreaCustomContainer"
                formik={formik}
                placeholder="Please Enter Task"
                fieldName={"task"}
                countClass={"taskCount"}
                limit={500}
              />
              {!!formik.values.postattachment && showPreview && (
                <AttachmentPreview
                  commentAttachmentPreview={"taskAttachmentPreview"}
                  attachment={formik.values.postattachment}
                  clearAttachment={clearAttachment}
                />
              )}
            </div>
            <div className="row my-3">
              <div className="col-sm-6">
                <CustomInput
                  inputClass="taskInputClass"
                  id={"priority"}
                  formGroup={false}
                  inputProps={{
                    placeholder: "Priority of your task",
                    type: "text",
                    ...formik.getFieldProps("priority"),
                    error: formik.errors.priority,
                  }}
                />
              </div>
              <div className="col-sm-6">
                <CustomInput
                  inputClass="taskInputClass"
                  id={"date"}
                  formGroup={false}
                  inputProps={{
                    type: "date",
                    ...formik.getFieldProps("date"),
                    error: formik.errors.date,
                  }}
                />
              </div>
            </div>
            <div className="taskOptions">
              <div className="selectMemberTask">
                <div style={{ paddingRight: "10px" }}>
                  <FontAwesomeIcon icon={faUserPlus} className=" me-1" />
                </div>
                <div style={{ paddingRight: "10px" }}>
                  <TeamMemberSelection
                    clearMembers={() => setSelectedMember([])}
                    post={false}
                    team_id={teamId}
                    selectedMember={selectedMember}
                    onMemberSelect={(member) => {
                      setSelectedMember([...selectedMember, member]);
                      addToast("Member is selected ", {
                        appearance: "success",
                      });
                    }}
                    discardMember={(member) => {
                      setSelectedMember(
                        selectedMember.filter((selectedMember) => {
                          return selectedMember.id !== member.id;
                        })
                      );
                      addToast("Member is removed ", {
                        appearance: "error",
                      });
                    }}
                    setSelectedMember={setSelectedMember}
                  />
                </div>
              </div>
              <AttachmentSelection onChange={onAttachmentSelected} />
            </div>
            <ButtonWithProgress
              isSubmitting={formik.isSubmitting}
              buttonWithProgressClass="btn grad_btn full_w mt-3"
              initialText="Create Task"
              progress={uploadProgress}
            />
          </form>
        </div>
      </Modal>
    </>
  );
};
